import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Stack,
  Avatar,
} from "@mui/material";
import {
  FitnessCenter as FitnessCenterIcon,
  SelfImprovement as RestIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import {
  getWorkoutDayColor,
  getRestDayColor,
} from "../theme/TrainingPlanTheme";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Overview of all workout days in a grid layout
 */
const WorkoutOverview = ({
  planData,
  onDaySelect,
  isRestDay,
  brandingColors,
}) => {
  const formatDayName = (index) => {
    return `Day ${index + 1}`;
  };

  const getExerciseCount = (split) => {
    if (isRestDay(split)) return 0;
    return split.exercises?.length || 0;
  };

  const getDayIcon = (split, index) => {
    if (isRestDay(split)) {
      return <RestIcon />;
    }
    return <FitnessCenterIcon />;
  };

  const getDayColor = (split, index) => {
    if (isRestDay(split)) {
      return getRestDayColor(brandingColors);
    }
    return getWorkoutDayColor(index, brandingColors);
  };

  const getTextColor = (backgroundColor) => {
    // Simple brightness calculation
    const hex = backgroundColor.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 140 ? "#000000" : "#ffffff";
  };

  if (!planData.splits || planData.splits.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          No workouts found in this training plan.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
        Training Schedule
      </Typography>

      <Grid container spacing={3}>
        {planData.splits.map((split, index) => {
          const restDay = isRestDay(split);
          const exerciseCount = getExerciseCount(split);
          const dayColor = getDayColor(split, index);
          const textColor = getTextColor(dayColor);

          return (
            <Grid item xs={12} sm={6} md={4} key={split.id || index}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  transition:
                    "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                  },
                  overflow: "hidden", // Prevent any layout issues
                }}
              >
                <CardActionArea
                  onClick={() => onDaySelect(index)}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    p: 0, // Remove default padding
                  }}
                >
                  {/* Header with day number and icon */}
                  <Box
                    sx={{
                      backgroundColor: dayColor,
                      color: textColor,
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexShrink: 0, // Prevent shrinking
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        sx={{
                          bgcolor: "rgba(255,255,255,0.2)",
                          color: textColor,
                          width: 32,
                          height: 32,
                        }}
                      >
                        {getDayIcon(split, index)}
                      </Avatar>
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{ fontWeight: 600 }}
                      >
                        {formatDayName(index)}
                      </Typography>
                    </Box>

                    {!restDay && exerciseCount > 0 && (
                      <Chip
                        label={`${exerciseCount} exercises`}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(255,255,255,0.2)",
                          color: textColor,
                          fontWeight: 500,
                        }}
                      />
                    )}
                  </Box>

                  <CardContent
                    sx={{
                      flexGrow: 1,
                      p: 3,
                      "&:last-child": {
                        paddingBottom: 3, // Override MUI's default last-child padding
                      },
                    }}
                  >
                    {/* Workout Name */}
                    <Typography
                      variant="h6"
                      component="h4"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        color: dayColor,
                        mb: 2,
                      }}
                    >
                      {split.name || (restDay ? "Rest Day" : "Workout")}
                    </Typography>

                    {/* Description */}
                    <Box
                      sx={{
                        mb: 2,
                        "& p": { mb: 1, lineHeight: 1.5 },
                        "& ul, & ol": { pl: 2, mb: 1 },
                        color: "text.secondary",
                      }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {split.description ||
                          (restDay
                            ? "Focus on recovery, rest, and regeneration. Take this day to let your body rest and rebuild."
                            : "Focus on proper form and controlled movements throughout each exercise.")}
                      </ReactMarkdown>
                    </Box>

                    {/* Workout Details */}
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      sx={{ gap: 1 }}
                    >
                      {split.warmup && (
                        <Chip
                          icon={<ScheduleIcon />}
                          label="Includes Warm-up"
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      )}

                      {restDay ? (
                        <Chip
                          icon={<RestIcon />}
                          label="Recovery Day"
                          size="small"
                          color="default"
                          variant="filled"
                        />
                      ) : (
                        exerciseCount > 0 && (
                          <Chip
                            icon={<FitnessCenterIcon />}
                            label={`${exerciseCount} Exercises`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )
                      )}
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* FAQ Section */}
      {planData.faqs_and_answers && planData.faqs_and_answers.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
            Frequently Asked Questions
          </Typography>

          <Grid container spacing={2}>
            {planData.faqs_and_answers.map((faq, index) => (
              <Grid item xs={12} key={index}>
                <Card elevation={1} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      component="h3"
                      gutterBottom
                      sx={{ fontWeight: 600, color: "primary.main" }}
                    >
                      {faq.q}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {faq.a}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default WorkoutOverview;
